<script setup lang="ts">
import type { OBJECT } from '@pkg/config/sanity/schemas';
import { logger } from '@pkg/logger';
import type { ModularBlock } from '@pkg/sanity-utils/types/modularContentBlocks';
import { modularBlocksConfigKey } from './injectionKeys';
import type { ModularBlocksConfig } from './types';

const props = withDefaults(defineProps<{
  blocks?: Array<ModularBlock>;
  type?: 'outer' | 'inner';
  /** When true, each top-level block gets `data-cms-depth` set to its index in `blocks`. */
  enableMetrics?: boolean;
  /** Optional override; otherwise uses app-provided config via `modularBlocksConfigKey`. */
  blocksConfig?: ModularBlocksConfig;
}>(), {
  blocks: () => [],
  type: 'inner',
  enableMetrics: false,
});

const injectedConfig = inject(modularBlocksConfigKey, null);
const modularBlocksConfig = computed(() => props.blocksConfig ?? injectedConfig);

if (import.meta.dev && !modularBlocksConfig.value) {
  logger.error(
    'ModularBlocksRenderer: no blocksConfig prop and no modularBlocksConfigKey inject. Provide config via a Nuxt plugin or pass :blocks-config.',
  );
}

const modularBlocksConfigFlat = computed(() => {
  const config = modularBlocksConfig.value;
  if (!config) {
    return {};
  }
  return { ...config.outer, ...config.inner };
});

function blockToComponent(blockName: OBJECT) {
  return modularBlocksConfigFlat.value[blockName]?.component ?? null;
}

function blockToName(blockName: OBJECT) {
  return modularBlocksConfigFlat.value[blockName]?.name ?? null;
}

function errorAtBoundary(error: unknown, block: ModularBlock) {
  logger.error(`We could not load modular block ${block._type} with key ${block._key}`, '\n', error);
}
</script>

<template>
  <div v-if="props.blocks" :class="`m-blocks m-blocks--${props.type}`">
    <div
      v-for="(block, index) in props.blocks ?? []"
      :key="block._key"
      class="m-blocks__item"
      :data-cms-depth="props.enableMetrics ? String(index) : undefined"
    >
      <NuxtErrorBoundary @error="(error) => errorAtBoundary(error, block)">
        <component
          :is="blockToComponent(block._type)"
          :class="blockToName(block._type)"
          :block="block"
          :config="modularBlocksConfigFlat[block._type]"
          :modular-renderer="modularBlocksConfig?.modularRenderer"
        />
      </NuxtErrorBoundary>
    </div>
  </div>
</template>

<style scoped>
/**
 * The vertical spacing between our inner blocks has the following rules:
 *
 * [1] All inner blocks have default 32px (2rem) between them
 * [2] FAQ dropdowns that follow FAQ dropdowns should have a vertical spacing of 0
 *
 * Hero
 * - [3] Text following title is 16px (1rem) top
 * - [4] Anything else has 24px (1.5rem) top
 *
 * Sections with images + CTA BANNER
 * - [5] Text that follows Title has 12px (0.75rem) top
 * - [6] Anything else that follows title has 24px (1.5rem) top
 *
 * Section without image (full width)
 * - [7] If Text under title, it has 12px top
 * - [7.5] If ticker under title, it has 24px (1.5rem) top
 *
 * Step block
 * - [8] Text following a title is 12px (0.75rem)
 * - [9] Anything else following a title is 24px (1.5rem)
 * - [10] Anything after text is 24px (1.5rem) top
 * - [11] Button Group is 40px (2.5rem) top
 *
 * CTA block
 * - [12] All elements have 12px (0.75rem) vertical spacing
 */
.m-blocks {
  --inner-block-v-spacing: 1.5rem; /* [1] */
  width: 100%;

  @screen tablet-wide {
    --inner-block-v-spacing: 2rem; /* [1] */
  }
}

.m-blocks__item {
  width: 100%;
}

.m-blocks--inner {
  > * + * {
    margin-top: var(--inner-block-v-spacing);
  }

  > :is(.inner-block-faq) + :is(.inner-block-faq) {
    margin-top: 0; /* [2] */
  }
}

:is(.outer-block-hero) .m-blocks--inner {
  > * + * {
    --inner-block-v-spacing: 1rem; /* [4] */

    @screen tablet-wide {
      --inner-block-v-spacing: 1.5rem; /* [4] */
    }
  }

  > :is(.inner-block-title) + :is(.inner-block-base-text, .inner-block-raw-html) {
    --inner-block-v-spacing: 0.75rem; /* [3] */
    @screen tablet-wide {
      --inner-block-v-spacing: 1rem; /* [3] */
    }
  }
}

:is(.outer-block-section, .c-section-block--no-image, .outer-block-cta) .m-blocks--inner {
  > :is(.inner-block-title) + * {
    --inner-block-v-spacing: 1rem; /* [6] */

    @screen tablet-wide {
      --inner-block-v-spacing: 1.5rem; /* [6] */
    }
  }

  > :is(.inner-block-title) + :is(.inner-block-base-text, .inner-block-raw-html) {
    --inner-block-v-spacing: 0.75rem; /* [5] */
  }
}

:is(.outer-block-section.c-section-block--image-left, .outer-block-section.c-section-block--image-right, .outer-block-sub-hero) .m-blocks--inner {
  > :is(.inner-block-title) + :is(.inner-block-base-text, .inner-block-raw-html) {
    --inner-block-v-spacing: theme('spacing.12'); /* [7] */
  }

  > :is(.inner-block-title) + :is(.inner-block-ticker) {
    --inner-block-v-spacing: theme('spacing.24'); /* [7.5] */
  }
}

:is(.outer-block-steps-section) .m-blocks--inner {
  > :is(.inner-block-title, .inner-block-base-text, .inner-block-raw-html) + * {
    --inner-block-v-spacing: 1rem; /* [9 & 10] */
    @screen tablet-wide {
      --inner-block-v-spacing: 1.5rem; /* [9 & 10] */
    }
  }

  > :is(.inner-block-title) + :is(.inner-block-base-text, .inner-block-raw-html) {
    --inner-block-v-spacing: 0.5rem; /* [8] */
    @screen tablet-wide {
      --inner-block-v-spacing: 0.75rem; /* [8] */
    }
  }

  > * + :is(.inner-block-button-group) {
    --inner-block-v-spacing: 2rem; /* [11] */

    @screen tablet-wide {
      --inner-block-v-spacing: 2.5rem; /* [11] */
    }
  }
}

:is(.outer-block-cta) {
  --inner-block-v-spacing: 0.75rem; /* [12] */
}

/**
 * Other Module Content rules relating to outer and inner blocks:
 *
 * Base Text Alignment
 * - [BTA-1] Base Text inside of a Sub Hero is always centered
 * - [BTA-2] Base Text inside of a Section with Image Left or Right is always centered on mobile, and left-aligned on desktop
 *
 * - [CTA-1] CTA block that preceeds Image/Video block should have extra spacing underneath it
 */

:is(.outer-block-sub-hero) .inner-block-base-text {
  text-align: center; /* [BTA-1] */
}

:is(.outer-block-section.c-section-block--image-left, .outer-block-section.c-section-block--image-right) .inner-block-base-text {
  text-align: center; /* [BTA-2] */

  @screen tablet-wide {
    text-align: left; /* [BTA-2] */
  }
}

:is(.outer-block-cta):has(+ .outer-block-image-video) {
  @screen tablet-wide {
    padding-bottom: 4.5rem; /* [CTA-1] */
  }
}

:is(.inner-block-title) {
  scroll-margin-top: calc(var(--header-height) * 1.2);
}
</style>
